/**
 * SPA source-cache types (aligned with @zunelo/shared SWR contracts).
 */

import type {
  ApiEnvelope,
  SourceCacheMeta,
  SourceCachePolicy,
  SourceCacheSource,
} from '@zunelo/shared';

export type { SourceCacheMeta, SourceCachePolicy, SourceCacheSource };

export interface SourceCacheEntry<T> {
  data: T;
  cache: SourceCacheMeta;
  serverTime?: string;
}

export interface SourceCacheAdapter {
  get<T>(key: string): SourceCacheEntry<T> | null;
  set<T>(key: string, entry: SourceCacheEntry<T>): void;
  delete(key: string): void;
  clear(): void;
}

export interface GetOrRevalidateOptions<T> {
  key: string;
  source: SourceCacheSource;
  policy: SourceCachePolicy;
  fetcher: () => Promise<ApiEnvelope<T>>;
  /** Bypass soft TTL and always hit the provider. */
  force?: boolean;
  nowMs?: number;
}

export interface GetOrRevalidateResult<T> {
  /** Immediate snapshot (cache hit) or provider result when miss/force. */
  data: T | undefined;
  envelope: ApiEnvelope<T> | undefined;
  cache: SourceCacheMeta | undefined;
  /** Resolves when background (or awaited) revalidation finishes. */
  revalidatePromise: Promise<ApiEnvelope<T>>;
  fromCache: boolean;
}

export function sourceCacheStorageKey(
  uid: string | undefined,
  source: SourceCacheSource,
  listKey = 'list',
): string {
  const scope = uid && uid.trim() ? uid.trim() : 'anon';
  return `${scope}:${source}:${listKey}`;
}
